import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {usePage, Link} from "@inertiajs/react";
import {useLaravelReactI18n} from "laravel-react-i18n";
import Icon from "@mdi/react";
import {mdiAlertCircleOutline} from "@mdi/js";
import {Badge} from "react-bootstrap";
import menuIcons from "../../menuIcons.js";
import {useMenueZustand} from "./MenueZustandContext";

/**
 * Schwebendes Untermenü für Gruppen im eingeklappten Hauptmenü (Paket S4,
 * Rückmeldung 22.09.2026). Die Fremdkomponente Sidebar.jsx rendert die
 * Unterpunkte einer Gruppe nur, wenn sie dort per Klick aufgeklappt ist
 * (eigener React-State im Paket) - das reicht für "beim Überfahren" nicht.
 *
 * Diese Komponente liest dieselben Menüdaten (geteilter Inertia-Prop
 * "menuEntries") unabhängig noch einmal, ordnet sie per Index den echten
 * DOM-Symbolen in der Seitenleiste zu und rendert das Untermenü bei Hover/
 * Fokus selbst - per Portal direkt an <body>, damit kein overflow einer
 * Fremdkomponente (Offcanvas, <main>) es abschneiden kann. An Sidebar.jsx
 * selbst ändert sich dadurch nichts.
 */

const TOP_NAV_SELECTOR = ".sidebar-nav .overflow-y-auto > .nav.flex-column";
const HOVER_VERZOEGERUNG_MS = 150;
const SCHLIESS_VERZOEGERUNG_MS = 250;

function renderSymbol(iconKey) {
    const icon = menuIcons && menuIcons[iconKey];

    if (!icon) {
        return <Icon path={mdiAlertCircleOutline} size={0.9} />;
    }

    if (typeof icon === "function") {
        const IconComponent = icon;
        return <IconComponent size="1.2em" />;
    }

    if (typeof icon === "string") {
        return <Icon path={icon} size={0.9} />;
    }

    return <Icon path={mdiAlertCircleOutline} size={0.9} />;
}

export default function MenueFlyoutController() {
    const {t} = useLaravelReactI18n();
    const {menuEntries, path} = usePage().props;
    const {eingeklappt} = useMenueZustand();

    const [offenerIndex, setOffenerIndex] = useState(null);
    const [position, setPosition] = useState(null);
    const schliessTimer = useRef(null);
    const oeffnenTimer = useRef(null);
    const panelRef = useRef(null);
    const zuletztFokussiertesElement = useRef(null);

    const menuItems = useMemo(() => {
        if (Array.isArray(menuEntries)) {
            return menuEntries;
        }

        return menuEntries ? Object.values(menuEntries) : [];
    }, [menuEntries]);

    const fullPath = "/" + (path || "");

    const positionBerechnen = useCallback((navItemElement) => {
        const rect = navItemElement.getBoundingClientRect();

        return {top: rect.top, left: rect.right + 8};
    }, []);

    const schliessenAbbrechen = useCallback(() => {
        if (schliessTimer.current) {
            window.clearTimeout(schliessTimer.current);
            schliessTimer.current = null;
        }
    }, []);

    const verzoegertSchliessen = useCallback(() => {
        schliessenAbbrechen();
        schliessTimer.current = window.setTimeout(() => {
            setOffenerIndex(null);
            setPosition(null);
        }, SCHLIESS_VERZOEGERUNG_MS);
    }, [schliessenAbbrechen]);

    const sofortOeffnen = useCallback(
        (index, element) => {
            if (oeffnenTimer.current) {
                window.clearTimeout(oeffnenTimer.current);
                oeffnenTimer.current = null;
            }
            schliessenAbbrechen();
            setOffenerIndex(index);
            setPosition(positionBerechnen(element));
        },
        [positionBerechnen, schliessenAbbrechen]
    );

    const verzoegertOeffnen = useCallback(
        (index, element) => {
            if (oeffnenTimer.current) {
                window.clearTimeout(oeffnenTimer.current);
            }
            oeffnenTimer.current = window.setTimeout(() => {
                sofortOeffnen(index, element);
            }, HOVER_VERZOEGERUNG_MS);
        },
        [sofortOeffnen]
    );

    const schliessenSofort = useCallback(() => {
        if (oeffnenTimer.current) {
            window.clearTimeout(oeffnenTimer.current);
            oeffnenTimer.current = null;
        }
        schliessenAbbrechen();
        setOffenerIndex(null);
        setPosition(null);
    }, [schliessenAbbrechen]);

    // Hover-Erkennung: Maus über einen Gruppen-Trigger im Streifen -
    // Delegation auf document, damit kein Ref auf die Fremdkomponente nötig ist.
    useEffect(() => {
        if (!eingeklappt) {
            return undefined;
        }

        const topNav = () => document.querySelector(TOP_NAV_SELECTOR);

        const indexZuElement = (target) => {
            const nav = topNav();
            const navItem = target.closest(".nav-item");

            if (!nav || !navItem || navItem.parentElement !== nav) {
                return null;
            }

            const index = Array.prototype.indexOf.call(nav.children, navItem);

            return index >= 0 ? {index, navItem} : null;
        };

        const handleMouseOver = (event) => {
            const treffer = indexZuElement(event.target);

            if (!treffer || !menuItems[treffer.index]?.children) {
                return;
            }

            verzoegertOeffnen(treffer.index, treffer.navItem);
        };

        const handleMouseOut = (event) => {
            const treffer = indexZuElement(event.target);

            if (!treffer) {
                return;
            }

            if (event.relatedTarget && event.target.closest(".nav-item")?.contains(event.relatedTarget)) {
                return;
            }

            if (oeffnenTimer.current) {
                window.clearTimeout(oeffnenTimer.current);
                oeffnenTimer.current = null;
            }

            verzoegertSchliessen();
        };

        const handleFocusIn = (event) => {
            const treffer = indexZuElement(event.target);

            if (!treffer || !menuItems[treffer.index]?.children) {
                return;
            }

            zuletztFokussiertesElement.current = event.target;
            sofortOeffnen(treffer.index, treffer.navItem);
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape" && offenerIndex !== null) {
                schliessenSofort();
                zuletztFokussiertesElement.current?.focus();
                return;
            }

            if (event.key !== "ArrowDown") {
                return;
            }

            const treffer = indexZuElement(event.target);

            if (!treffer || !menuItems[treffer.index]?.children) {
                return;
            }

            event.preventDefault();
            sofortOeffnen(treffer.index, treffer.navItem);
            window.setTimeout(() => {
                panelRef.current?.querySelector("a")?.focus();
            }, 0);
        };

        document.addEventListener("mouseover", handleMouseOver);
        document.addEventListener("mouseout", handleMouseOut);
        document.addEventListener("focusin", handleFocusIn);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mouseover", handleMouseOver);
            document.removeEventListener("mouseout", handleMouseOut);
            document.removeEventListener("focusin", handleFocusIn);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [eingeklappt, menuItems, verzoegertOeffnen, verzoegertSchliessen, sofortOeffnen, schliessenSofort, offenerIndex]);

    // Beim Ausklappen des Hauptmenüs (oder Seitenwechsel) sicher schließen.
    useEffect(() => {
        if (!eingeklappt) {
            schliessenSofort();
        }
    }, [eingeklappt, schliessenSofort]);

    useEffect(() => {
        schliessenSofort();
        // Seitenwechsel schließt ein offenes Flyout - eslint: schliessenSofort ist stabil (useCallback ohne veränderliche Deps).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path]);

    if (!eingeklappt || offenerIndex === null || !position) {
        return null;
    }

    const gruppe = menuItems[offenerIndex];

    if (!gruppe || !gruppe.children) {
        return null;
    }

    return createPortal(
        <div
            ref={panelRef}
            className="menue-flyout-panel"
            style={{top: position.top, left: position.left}}
            role="menu"
            aria-label={t(gruppe.title)}
            onMouseEnter={schliessenAbbrechen}
            onMouseLeave={verzoegertSchliessen}
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    schliessenSofort();
                    zuletztFokussiertesElement.current?.focus();
                    return;
                }

                if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
                    return;
                }

                event.preventDefault();
                const links = Array.from(panelRef.current?.querySelectorAll("a") || []);
                const aktuellerIndex = links.indexOf(document.activeElement);
                const naechsterIndex =
                    event.key === "ArrowDown"
                        ? (aktuellerIndex + 1) % links.length
                        : (aktuellerIndex - 1 + links.length) % links.length;

                links[naechsterIndex]?.focus();
            }}
        >
            <div className="menue-flyout-titel">{t(gruppe.title)}</div>
            {gruppe.children.map((kind, index) => {
                const istAktiv = kind.href && fullPath.startsWith(kind.href);

                return (
                    <Link
                        key={kind.href || index}
                        href={kind.href}
                        role="menuitem"
                        className={`menue-flyout-link${istAktiv ? " active" : ""}`}
                        onClick={schliessenSofort}
                    >
                        <span className="menue-flyout-icon">{renderSymbol(kind.icon)}</span>
                        <span>{t(kind.title)}</span>
                        {kind.badge && (
                            <Badge pill bg={kind.badge.type} className="ms-auto">
                                {kind.badge.content}
                            </Badge>
                        )}
                    </Link>
                );
            })}
        </div>,
        document.body
    );
}
